'use client'

import Link from 'next/link'
import {
  Security as Shield,
  BareMetalServer as Server,
  Box,
  Download,
  Locked as Lock,
  Chip as Cpu,
  View as Eye,
  SettingsAdjust as Sliders,
  CheckmarkOutline as CheckCircle,
  type CarbonIconType,
} from '@carbon/icons-react'

import {
  EASE,
  Eyebrow,
  HAIRLINE,
  PANEL,
  useReveal,
} from '@/components/page-kit'
import { ChromaText } from '@/components/ui/chroma-text'
import { cn } from '@/lib/utils'

/* Platform overview: content column beside the "Built for real-world use"
   panel. The old build hard-coded a slate-900 gradient with blurred glow
   orbs and icon chip boxes; this sets the same copy on the page tokens —
   features as a ruled list, benefits as ruled rows inside one PANEL. */

const features: {
  title: string
  desc: string
  icon: CarbonIconType
}[] = [
  {
    title: 'Complete school ecosystem',
    desc: 'Everything from admissions to alumni management in one unified platform.',
    icon: Box,
  },
  {
    title: 'Flexible deployment options',
    desc: "Choose what works best for your institution's infrastructure and requirements.",
    icon: Server,
  },
  {
    title: 'Scalable for every need',
    desc: 'Grow from a single branch to multiple campuses without changing your system.',
    icon: Shield,
  },
  {
    title: 'Complete data ownership',
    desc: 'Your information stays yours. Export, backup, or migrate anytime you need.',
    icon: Download,
  },
]

const benefits: {
  title: string
  desc: string
  icon: CarbonIconType
}[] = [
  {
    title: 'Bank-Grade Security',
    desc: 'Your data protected with encryption, role-based access, and detailed activity logs',
    icon: Lock,
  },
  {
    title: 'Fully customizable & Easy to manage',
    desc: "Handle complex tasks with simple workflows. Configure everything to match your school's unique needs.",
    icon: Sliders,
  },
  {
    title: 'Reliable & Consistent',
    desc: 'Designed for daily school operations with thousands of transactions daily',
    icon: CheckCircle,
  },
  {
    title: 'Complete Transparency',
    desc: 'Clear visibility into every process with real-time updates and audit trails',
    icon: Eye,
  },
  {
    title: 'Lightning Performance',
    desc: 'Optimized for Indian schools with fast response times even during peak hours',
    icon: Cpu,
  },
]

export function OpenSource() {
  const { ref, shown } = useReveal<HTMLDivElement>(0.15)

  const enter = (delay: number) => ({
    opacity: shown ? 1 : 0,
    transform: shown ? 'translateY(0)' : 'translateY(24px)',
    transition: `opacity 0.7s ${EASE} ${delay}ms, transform 0.7s ${EASE} ${delay}ms`,
  })

  return (
    <section
      id="capabilities"
      className="bg-background py-16 sm:py-20 md:py-28"
    >
      <div
        ref={ref}
        className="container mx-auto grid items-start gap-12 lg:grid-cols-2 lg:gap-16"
      >
        {/* ── Left column — content ── */}
        <div style={enter(0)}>
          <Eyebrow>Platform Overview</Eyebrow>

          <h2 className="font-heading mt-4 text-2xl font-normal leading-tight text-foreground sm:text-3xl md:text-4xl lg:text-5xl">
            <ChromaText>A complete system for modern education.</ChromaText>
          </h2>

          <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base md:text-[17px]">
            Everything you need to manage your institution efficiently, from
            daily operations to long-term planning, all in one place.
          </p>

          {/* Ruled feature list — icon set straight on the page, no chip box */}
          <div
            className={cn(
              'mt-10 divide-y border-y',
              HAIRLINE,
              'divide-border/70'
            )}
          >
            {features.map(({ title, desc, icon: Icon }) => (
              <div key={title} className="flex gap-4 py-5">
                <Icon
                  aria-hidden
                  size={20}
                  className="mt-0.5 shrink-0 text-emerald-600 dark:text-primary-active"
                />
                <div>
                  <h3 className="text-[15px] font-semibold text-foreground">
                    {title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                    {desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* The three pages a school actually asks about once it has read the
              list above — each was reachable from the sitemap and nowhere else. */}
          <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
            Coming from another system? The{' '}
            <Link
              href="/migration-service"
              className="font-medium text-foreground underline decoration-emerald-600/40 underline-offset-4 transition-colors hover:decoration-emerald-600 dark:decoration-primary-active/40 dark:hover:decoration-primary-active"
            >
              data migration service
            </Link>{' '}
            moves your existing student, staff and finance records across, the{' '}
            <Link
              href="/implementation"
              className="font-medium text-foreground underline decoration-emerald-600/40 underline-offset-4 transition-colors hover:decoration-emerald-600 dark:decoration-primary-active/40 dark:hover:decoration-primary-active"
            >
              implementation process
            </Link>{' '}
            sets out what each stage involves, and{' '}
            <Link
              href="/udise-apaar"
              className="font-medium text-foreground underline decoration-emerald-600/40 underline-offset-4 transition-colors hover:decoration-emerald-600 dark:decoration-primary-active/40 dark:hover:decoration-primary-active"
            >
              UDISE+ and APAAR ID integration
            </Link>{' '}
            covers the reporting Indian schools have to file.
          </p>
        </div>

        {/* ── Right column — benefits panel ── */}
        <div style={enter(100)}>
          <div className={cn('overflow-hidden rounded-xl', PANEL)}>
            <div className={cn('border-b px-6 py-6 sm:px-8', HAIRLINE)}>
              <h3 className="font-heading text-xl font-normal text-foreground md:text-2xl">
                Built for real-world use
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Every feature designed with practical needs in mind
              </p>
            </div>

            <div className="divide-y divide-border/70">
              {benefits.map(({ title, desc, icon: Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-3.5 px-6 py-4 transition-colors hover:bg-accent/60 sm:px-8"
                >
                  <Icon
                    aria-hidden
                    size={16}
                    className="mt-0.5 shrink-0 text-emerald-600 dark:text-primary-active"
                  />
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {title}
                    </p>
                    <p className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground">
                      {desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
